<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vs-tabs v-model="activeTab">
                <vs-tab label="List" icon-pack="feather" icon="icon-list">
                    <div class="mt-6">
                        <!-- Table Section Header -->
                        <vs-row class="mb-4">
                            <vs-col vs-w="6">
                                <h4 class="mt-2">Budget Master List</h4>
                            </vs-col>
                            <vs-col vs-w="6" class="text-right flex justify-end">
                                <vs-button color="primary" type="filled" @click="activeTab = 1; resetForm();" icon-pack="feather" icon="icon-plus" class="mr-2">Add New</vs-button>
                                <vx-tooltip text="Refresh Data" color="primary">
                                    <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                                        <font-awesome-icon :icon="redoIcon" />
                                    </vs-button>
                                </vx-tooltip>
                            </vs-col>
                        </vs-row>

                        <!-- Search and Filter Row -->
                        <vs-row class="mb-3">
                            <vs-col vs-xs="12" vs-sm="12" vs-lg="4" class="px-0 pr-2 mb-2">
                                <vs-input
                                    class="w-full"
                                    placeholder="Search budget master..."
                                    v-model="filters.search"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="px-0 pr-2 mb-2">
                                <SelectForeign
                                    :model.sync="filters.filter_year"
                                    label=""
                                    placeholder="Filter by Financial Year"
                                    :multiple="false"
                                    :load_initial="true"
                                    :label_keys="['fy_year']"
                                    :show_id_in_label="false"
                                    :endpoint="fyEndpoint"
                                    container_class="w-full"
                                    input_class=""
                                    :use_id_as_value="true"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="px-0 mb-2">
                                <SelectForeign
                                    :model.sync="filters.filter_item_group"
                                    label=""
                                    placeholder="Filter by Item Group"
                                    :multiple="false"
                                    :load_initial="true"
                                    :label_keys="['code']"
                                    :show_id_in_label="false"
                                    :endpoint="itemGroupEndpoint"
                                    container_class="w-full"
                                    input_class=""
                                    :use_id_as_value="true"
                                    :result_filter="item => item.is_active"
                                />
                            </vs-col>
                        </vs-row>
                        
                        <vx-card>
                            <div v-if="loading" class="text-center p-4">
                                <h3>Loading Data.. Please wait..</h3>
                            </div>
                            
                            <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe v-if="!loading" noDataText="">
                                <vs-tr>
                                    <vs-th style="min-width: 80px;">S.No</vs-th>
                                    <vs-th style="min-width: 150px;">Financial Year</vs-th>
                                    <vs-th style="min-width: 150px;">Item Group Code</vs-th>
                                    <vs-th style="min-width: 200px;">Item Group Description</vs-th>
                                    <vs-th style="min-width: 150px;">Life Cycle Cost</vs-th>
                                    <vs-th style="min-width: 150px;">Planned Cost</vs-th>
                                    <vs-th style="min-width: 150px;">Additional Budget</vs-th>
                                    <vs-th style="min-width: 100px;">Action</vs-th>
                                </vs-tr>
                                <template v-if="results.length > 0">
                                    <template v-for="(item, index) in results">
                                        <vs-tr :key="index">
                                            <vs-td>
                                                {{ filters.offset + index + 1 }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getYearDisplay(item.year) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getItemGroupCode(item.item_group) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getItemGroupDescription(item.item_group) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.life_cycle_cost || '0.00' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.planned_cost || '0.00' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.additional_budget || '0.00' }}
                                            </vs-td>
                                            <vs-td>
                                                <vs-button @click="editItem(item)" color="primary" type="border" icon="icon-edit" icon-pack="feather" size="small"></vs-button>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </template>
                                <template v-else>
                                    <vs-tr>
                                        <vs-td colspan="8" class="text-center p-4">
                                            <p class="text-muted mb-0" v-if="has_active_filters">No results found for the current search/filter criteria.</p>
                                            <p class="text-muted mb-0" v-else>No data available</p>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                        </vx-card>
                        
                        <!-- Pagination -->
                        <template v-if="total_results">
                            <vs-row class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </div>
                </vs-tab>

                <vs-tab :label="editingId ? 'Edit' : 'Add'" icon-pack="feather" :icon="editingId ? 'icon-edit' : 'icon-plus'">
                    <div class="mt-6">
                        <!-- Form Section -->
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'Edit Budget Master' : 'Create Budget Master' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new budget master entry.</p>
                                <p class="text-muted mb-4" v-else>Update the budget master details below.</p>
                            </div>
                            
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <SelectForeign
                                        :model.sync="formData.year"
                                        label="Financial Year *"
                                        placeholder="Type to Search..."
                                        :multiple="false"
                                        :load_initial="true"
                                        :label_keys="['fy_year']"
                                        :show_id_in_label="false"
                                        :endpoint="fyEndpoint"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="true"
                                        :close_on_select="true"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.year">
                                        {{ (api_errors.year || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <SelectForeign
                                        :model.sync="formData.item_group"
                                        label="Item Group Code *"
                                        placeholder="Type to Search..."
                                        :multiple="false"
                                        :load_initial="true"
                                        :label_keys="['code']"
                                        :show_id_in_label="false"
                                        :endpoint="itemGroupEndpoint"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="true"
                                        :close_on_select="true"
                                        :result_filter="item => item.is_active"
                                        @change="updateItemGroupDescription"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.item_group">
                                        {{ (api_errors.item_group || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model="itemGroupDescription"
                                        label="Item Group Description"
                                        placeholder="Auto-filled from selection"
                                        class="w-full"
                                        disabled
                                    />
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model.number="formData.life_cycle_cost"
                                        label="Life Cycle Cost"
                                        type="number"
                                        step="0.01"
                                        placeholder="e.g., 1000.0"
                                        class="w-full"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.life_cycle_cost">
                                        {{ (api_errors.life_cycle_cost || []).join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>
                            
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model.number="formData.planned_cost"
                                        label="Planned Cost"
                                        type="number"
                                        step="0.01"
                                        placeholder="e.g., 500.0"
                                        class="w-full"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.planned_cost">
                                        {{ (api_errors.planned_cost || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model.number="formData.additional_budget"
                                        label="Additional Budget"
                                        type="number"
                                        step="0.01"
                                        placeholder="e.g., 200.0"
                                        class="w-full"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.additional_budget">
                                        {{ (api_errors.additional_budget || []).join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>
                            
                            <vs-row class="mt-4">
                                <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                                    <vs-button @click="resetForm(); activeTab = 0;" color="warning" type="flat" class="mr-2" v-if="editingId">
                                        Cancel
                                    </vs-button>
                                    <vs-button @click="save" :disabled="submit_disabled" color="primary" type="filled">
                                        {{ editingId ? 'Update' : 'Save' }}
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                        </vx-card>
                    </div>
                </vs-tab>
            </vs-tabs>
        </template>
    </div>
</template>

<script>
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../utils/requests';
import SelectForeign from '../../components/letstream/ls-select-foreign/SelectForeign.vue';

export default {
    components: {
        SelectForeign
    },
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);
        
        let current_page = offset == 0 ? 1 : Math.ceil(offset / limit) + 1;
        return {
            results: [],
            loading: false,
            submit_disabled: false,
            api_errors: {},
            editingId: null,
            total_results: null,
            current_page: current_page,
            itemGroupDescription: '',
            itemGroupMap: {}, // Map to store item group ID to description
            itemGroupCodeMap: {}, // Map to store item group ID to code
            yearMap: {}, // Map to store year ID to fy_year
            activeTab: 0,
            filters: {
                search: this.$route.query.search || null,
                filter_year: this.$route.query.filter_year ? parseInt(this.$route.query.filter_year) : null,
                filter_item_group: this.$route.query.filter_item_group ? parseInt(this.$route.query.filter_item_group) : null,
                offset: offset,
                limit: limit
            },
            formData: {
                year: null,
                item_group: null,
                life_cycle_cost: null,
                planned_cost: null,
                additional_budget: null
            }
        }
    },
    computed: {
        redoIcon: () => faRedo,
        isAdmin() {
            return true;
        },
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        fyEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_year}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_year}?is_active=true`;
        },
        itemGroupEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_item_group}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_item_group}?is_active=true`;
        },
        has_active_filters() {
            return !!(this.filters.search || this.filters.filter_year || this.filters.filter_item_group)
        }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ 
                        name: this.$route.name, 
                        query: {
                            offset: newV.offset,
                            limit: newV.limit,
                            search: newV.search || undefined,
                            filter_year: newV.filter_year || undefined,
                            filter_item_group: newV.filter_item_group || undefined
                        } 
                    });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.loadData();
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        }
    },
    mounted() {
        this.loadItemGroupData()
        this.loadYearData()
        this.init()
    },
    methods: {
        loadYearData() {
            // Load Year master data to create ID to fy_year mapping
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            let url = orgId ? `${this.$store.state.apiendpoints.master_year}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_year}?is_active=true`;
            
            Request(this, 'GET', url, {limit: 1000}).then(
                (res) => {
                    let yearList = [];
                    if (res && res.results && Array.isArray(res.results)) {
                        yearList = res.results;
                    } else if (res && res.data && res.data.results) {
                        yearList = res.data.results;
                    } else if (Array.isArray(res)) {
                        yearList = res;
                    } else if (res && res.data && Array.isArray(res.data)) {
                        yearList = res.data;
                    }
                    
                    // Create map: yearMap[id] = fy_year
                    this.yearMap = {};
                    yearList.forEach(year => {
                        if (year.id && year.fy_year) {
                            this.yearMap[year.id] = year.fy_year;
                        }
                    });
                },
                (err) => {
                    // Silently fail
                }
            )
        },
        loadItemGroupData() {
            // Load Item Group master data to create ID to description and code mapping
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            let url = orgId ? `${this.$store.state.apiendpoints.master_item_group}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_item_group}?is_active=true`;
            
            Request(this, 'GET', url, {limit: 1000}).then(
                (res) => {
                    let itemGroupList = [];
                    if (res && res.results && Array.isArray(res.results)) {
                        itemGroupList = res.results;
                    } else if (res && res.data && res.data.results) {
                        itemGroupList = res.data.results;
                    } else if (Array.isArray(res)) {
                        itemGroupList = res;
                    } else if (res && res.data && Array.isArray(res.data)) {
                        itemGroupList = res.data;
                    }
                    
                    // Create maps: itemGroupMap[id] = description, itemGroupCodeMap[id] = code
                    this.itemGroupMap = {};
                    this.itemGroupCodeMap = {};
                    itemGroupList.forEach(item => {
                        if (item.id) {
                            if (item.description) {
                                this.itemGroupMap[item.id] = item.description;
                            }
                            if (item.code) {
                                this.itemGroupCodeMap[item.id] = item.code;
                            }
                        }
                    });
                },
                (err) => {
                    // Silently fail, description will just be empty
                }
            )
        },
        updateItemGroupDescription() {
            // Update description when item group is selected
            if (this.formData.item_group && this.itemGroupMap[this.formData.item_group]) {
                this.itemGroupDescription = this.itemGroupMap[this.formData.item_group];
            } else {
                this.itemGroupDescription = '';
            }
        },
        loadData() {
            this.loading = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_budget
            
            let payload = {
                offset: this.filters.offset,
                limit: this.filters.limit
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            if (this.filters.search) payload.search = this.filters.search
            if (this.filters.filter_year) payload.year = this.filters.filter_year
            if (this.filters.filter_item_group) payload.item_group = this.filters.filter_item_group
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    // Handle response structure: {status: true, data: {count, results: [...]}} or nested
                    if (res && res.data && res.data.results && Array.isArray(res.data.results)) {
                        // Nested response: {status: true, data: {count, results: [...]}}
                        this.results = res.data.results
                        this.total_results = res.data.count || 0
                    } else if (res && res.results && Array.isArray(res.results)) {
                        // Standard paginated response: {count, results: [...]}
                        this.results = res.results
                        this.total_results = res.count || 0
                    } else if (Array.isArray(res)) {
                        // Direct array response
                        this.results = res
                        this.total_results = res.length
                    } else if (res && res.data && Array.isArray(res.data)) {
                        // Nested array: {data: [...]}
                        this.results = res.data
                        this.total_results = res.data.length
                    } else {
                        this.results = []
                        this.total_results = 0
                    }
                    this.calculate_page()
                    this.loading = false
                    this.$vs.loading.close()
                },
                (err) => {
                    this.loading = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An error occurred while loading data. Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        save() {
            if (!this.formData.year || !this.formData.item_group) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fill all required fields!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            if (!this.$store.state.organisation.organisation || !this.$store.state.organisation.organisation.id) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Organisation not found!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            this.submit_disabled = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_budget
            let method = 'POST'
            
            if (this.editingId) {
                url += this.editingId + '/'
                method = 'PUT'
            }
            
            let payload = {
                organisation: this.$store.state.organisation.organisation.id,
                year: parseInt(this.formData.year) || this.formData.year,
                item_group: parseInt(this.formData.item_group) || this.formData.item_group,
                life_cycle_cost: parseFloat(this.formData.life_cycle_cost || 0),
                planned_cost: parseFloat(this.formData.planned_cost || 0),
                additional_budget: parseFloat(this.formData.additional_budget || 0)
            }
            
            Request(this, method, url, null, payload).then(
                (res) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: this.editingId ? "Updated Successfully!" : "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                    this.resetForm()
                    this.loadData()
                    this.activeTab = 0
                },
                (err) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    if (err.error) {
                        this.api_errors = err.error
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        editItem(item) {
            this.editingId = item.id
            
            // Handle both ID and object for foreign keys
            const yearId = (typeof item.year === 'object' && item.year) ? item.year.id : item.year
            const itemGroupId = (typeof item.item_group === 'object' && item.item_group) ? item.item_group.id : item.item_group
            
            this.formData = {
                year: yearId,
                item_group: itemGroupId,
                life_cycle_cost: item.life_cycle_cost,
                planned_cost: item.planned_cost,
                additional_budget: item.additional_budget
            }
            
            // Update description based on selected group
            this.updateItemGroupDescription()
            
            this.activeTab = 1
            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        resetForm() {
            this.editingId = null
            this.formData = {
                year: null,
                item_group: null,
                life_cycle_cost: null,
                planned_cost: null,
                additional_budget: null
            }
            this.itemGroupDescription = ''
            this.api_errors = {}

            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        calculate_page() {
            if (this.filters.offset == 0) {
                this.current_page = 1;
            } else {
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit) + 1;
            }
        },
        getYearDisplay(yearId) {
            // Handle both ID (number) and nested object
            if (typeof yearId === 'object' && yearId && yearId.fy_year) {
                return yearId.fy_year;
            } else if (yearId && this.yearMap[yearId]) {
                return this.yearMap[yearId];
            }
            return null;
        },
        getItemGroupCode(itemGroupId) {
            // Handle both ID (number) and nested object
            if (typeof itemGroupId === 'object' && itemGroupId && itemGroupId.code) {
                return itemGroupId.code;
            } else if (itemGroupId && this.itemGroupCodeMap[itemGroupId]) {
                return this.itemGroupCodeMap[itemGroupId];
            }
            return null;
        },
        getItemGroupDescription(itemGroupId) {
            // Handle both ID (number) and nested object
            if (typeof itemGroupId === 'object' && itemGroupId && itemGroupId.description) {
                return itemGroupId.description;
            } else if (itemGroupId && this.itemGroupMap[itemGroupId]) {
                return this.itemGroupMap[itemGroupId];
            }
            return null;
        },
        init() {
            let offset = parseInt(this.$route.query.offset || 0);
            let limit = parseInt(this.$route.query.limit || 20);
            this.filters.offset = offset;
            this.filters.limit = limit;
            this.filters.search = this.$route.query.search || null;
            this.filters.filter_year = this.$route.query.filter_year ? parseInt(this.$route.query.filter_year) : null;
            this.filters.filter_item_group = this.$route.query.filter_item_group ? parseInt(this.$route.query.filter_item_group) : null;
            this.calculate_page();
            
            if (!this.$route.query.offset || !this.$route.query.limit) {
                this.$router.replace({ 
                    name: this.$route.name, 
                    query: { 
                        ...this.$route.query,
                        offset: this.filters.offset, 
                        limit: this.filters.limit 
                    } 
                });
            }
            
            this.loadData();
        }
    }
}
</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.mb-4 {
    margin-bottom: 1.5rem;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.mt-4 {
    margin-top: 1.5rem;
}
</style>

